import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";
import {
  Users,
  DollarSign,
  FileText,
  Download,
  Plus,
  Edit,
  Check,
  Calendar,
  Building,
  Briefcase,
  Mail,
  Trash2,
  Send,
} from "lucide-react";
import { useAuth } from "../../App";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const DEPARTMENTS = [
  "Sales", "Administration", "Finance", "Operations", "Marketing", "IT", "HR"
];

const PayrollDashboard = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [payrollHistory, setPayrollHistory] = useState([]);
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("employees");
  const isAdmin = user?.role === "admin";
  
  // Employee form
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeForm, setEmployeeForm] = useState({
    employee_code: "",
    name: "",
    email: "",
    phone: "",
    department: "",
    role: "",
    employee_type: "staff",
    contract_type: "permanent",
    hire_date: "",
    bank_name: "",
    account_number: "",
    branch_code: "",
    gross_salary: "",
    salary_band: "",
    commission_rate: "0"
  });
  
  // Payroll generation
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(MONTHS[new Date().getMonth()]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(MONTHS[new Date().getMonth()]);
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [payrollSummary, setPayrollSummary] = useState(null);
  
  // Deletion request
  const [deletionDialogOpen, setDeletionDialogOpen] = useState(false);
  const [deletionTarget, setDeletionTarget] = useState(null);
  const [deletionReason, setDeletionReason] = useState("");

  useEffect(() => {
    fetchEmployees();
    fetchPayrollHistory();
    if (isAdmin) {
      fetchDeletionRequests();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (activeTab === "payroll") {
      fetchPayrollRecords(viewMonth, viewYear);
      fetchPayrollSummary(viewMonth, viewYear);
    }
  }, [activeTab, viewMonth, viewYear]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/payroll/employees`, { withCredentials: true });
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayrollRecords = async (month, year) => {
    try {
      const response = await axios.get(`${API}/payroll/records?month=${month}&year=${year}`, { withCredentials: true });
      setPayrollRecords(response.data);
    } catch (error) {
      console.error("Error fetching payroll records:", error);
    }
  };

  const fetchPayrollSummary = async (month, year) => {
    try {
      const response = await axios.get(`${API}/payroll/summary?month=${month}&year=${year}`, { withCredentials: true });
      setPayrollSummary(response.data);
    } catch (error) {
      console.error("Error fetching summary:", error);
    }
  };

  const fetchPayrollHistory = async () => {
    try {
      const response = await axios.get(`${API}/payroll/history`, { withCredentials: true });
      setPayrollHistory(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  };

  const fetchDeletionRequests = async () => {
    try {
      const response = await axios.get(`${API}/payroll/deletion-requests`, { withCredentials: true });
      setDeletionRequests(response.data);
    } catch (error) {
      console.error("Error fetching deletion requests:", error);
    }
  };

  const handleEmployeeSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...employeeForm,
        gross_salary: parseFloat(employeeForm.gross_salary) || 0,
        commission_rate: parseFloat(employeeForm.commission_rate) || 0
      };

      if (editingEmployee) {
        await axios.put(`${API}/payroll/employees/${editingEmployee.employee_id}`, data, { withCredentials: true });
        toast.success("Employee updated successfully");
      } else {
        await axios.post(`${API}/payroll/employees`, data, { withCredentials: true });
        toast.success("Employee added successfully");
      }
      
      setEmployeeDialogOpen(false);
      resetEmployeeForm();
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save employee");
    }
  };

  const resetEmployeeForm = () => {
    setEditingEmployee(null);
    setEmployeeForm({
      employee_code: "",
      name: "",
      email: "",
      phone: "",
      department: "",
      role: "",
      employee_type: "staff",
      contract_type: "permanent",
      hire_date: "",
      bank_name: "",
      account_number: "",
      branch_code: "",
      gross_salary: "",
      salary_band: "",
      commission_rate: "0"
    });
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setEmployeeForm({
      employee_code: employee.employee_code || "",
      name: employee.name || "",
      email: employee.email || "",
      phone: employee.phone || "",
      department: employee.department || "",
      role: employee.role || "",
      employee_type: employee.employee_type || "staff",
      contract_type: employee.contract_type || "permanent",
      hire_date: employee.hire_date || "",
      bank_name: employee.bank_name || "",
      account_number: employee.account_number || "",
      branch_code: employee.branch_code || "",
      gross_salary: employee.gross_salary?.toString() || "",
      salary_band: employee.salary_band || "",
      commission_rate: employee.commission_rate?.toString() || "0"
    });
    setEmployeeDialogOpen(true);
  };

  const handleGeneratePayroll = async () => {
    try {
      const response = await axios.post(
        `${API}/payroll/records/generate?month=${selectedMonth}&year=${selectedYear}`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setGenerateDialogOpen(false);
      setViewMonth(selectedMonth);
      setViewYear(selectedYear);
      fetchPayrollRecords(selectedMonth, selectedYear);
      fetchPayrollSummary(selectedMonth, selectedYear);
      fetchPayrollHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate payroll");
    }
  };

  const handleMarkAsPaid = async (recordId) => {
    try {
      await axios.put(`${API}/payroll/records/${recordId}/pay`, {}, { withCredentials: true });
      toast.success("Marked as paid");
      fetchPayrollRecords(viewMonth, viewYear);
      fetchPayrollSummary(viewMonth, viewYear);
      fetchPayrollHistory();
    } catch (error) {
      toast.error("Failed to mark as paid");
    }
  };

  const handleMarkAllAsPaid = async () => {
    try {
      const response = await axios.put(
        `${API}/payroll/records/pay-all?month=${viewMonth}&year=${viewYear}`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      fetchPayrollRecords(viewMonth, viewYear);
      fetchPayrollSummary(viewMonth, viewYear);
      fetchPayrollHistory();
    } catch (error) {
      toast.error("Failed to mark all as paid");
    }
  };

  const handleDownloadPayslip = async (recordId, employeeName) => {
    try {
      const response = await axios.get(`${API}/payroll/records/${recordId}/payslip`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payslip_${employeeName.replace(/\s+/g, '_')}_${viewMonth}_${viewYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Payslip downloaded");
    } catch (error) {
      toast.error("Failed to download payslip");
    }
  };

  const handleSendPayslipEmail = async (recordId, employeeName) => {
    try {
      await axios.post(`${API}/payroll/records/${recordId}/send-payslip`, {}, { withCredentials: true });
      toast.success(`Payslip sent to ${employeeName}`);
      fetchPayrollRecords(viewMonth, viewYear);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send payslip");
    }
  };

  const handleSendAllPayslips = async () => {
    try {
      const response = await axios.post(
        `${API}/payroll/records/send-all-payslips?month=${viewMonth}&year=${viewYear}`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      fetchPayrollRecords(viewMonth, viewYear);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send payslips");
    }
  };

  const openDeletionDialog = (employee) => {
    setDeletionTarget(employee);
    setDeletionReason("");
    setDeletionDialogOpen(true);
  };

  const handleRequestDeletion = async () => {
    if (!deletionTarget) return;
    
    try {
      const response = await axios.post(
        `${API}/payroll/employees/${deletionTarget.employee_id}/request-deletion`,
        { reason: deletionReason },
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setDeletionDialogOpen(false);
      setDeletionTarget(null);
      setDeletionReason("");
      fetchEmployees();
      if (isAdmin) fetchDeletionRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request deletion");
    }
  };

  const handleApproveDeletion = async (requestId) => {
    try {
      await axios.put(`${API}/payroll/deletion-requests/${requestId}/approve`, {}, { withCredentials: true });
      toast.success("Employee deactivated");
      fetchEmployees();
      fetchDeletionRequests();
    } catch (error) {
      toast.error("Failed to approve deletion");
    }
  };

  const handleRejectDeletion = async (requestId) => {
    try {
      await axios.put(`${API}/payroll/deletion-requests/${requestId}/reject`, {}, { withCredentials: true });
      toast.success("Deletion request rejected");
      fetchDeletionRequests();
    } catch (error) {
      toast.error("Failed to reject deletion");
    }
  };

  const formatCurrency = (amount) => {
    return `K ${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="payroll-dashboard">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-serif">Payroll Management</h2>
          <p className="text-muted-foreground text-sm">Manage employee salaries and payments</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Employees</p>
                <p className="text-2xl font-bold">{employees.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Gross</p>
                <p className="text-2xl font-bold">{formatCurrency(payrollSummary?.total_gross)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <FileText className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Deductions</p>
                <p className="text-2xl font-bold">{formatCurrency(payrollSummary?.total_deductions)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Net Pay</p>
                <p className="text-2xl font-bold">{formatCurrency(payrollSummary?.total_net)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="employees">Employees</TabsTrigger>
          <TabsTrigger value="payroll">Payroll</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          {isAdmin && deletionRequests.length > 0 && (
            <TabsTrigger value="requests" className="text-red-500">
              Deletion Requests ({deletionRequests.length})
            </TabsTrigger>
          )}
        </TabsList>

        {/* Employees Tab */}
        <TabsContent value="employees" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={employeeDialogOpen} onOpenChange={(open) => {
              setEmployeeDialogOpen(open);
              if (!open) resetEmployeeForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-employee-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Employee
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingEmployee ? "Edit Employee" : "Add New Employee"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleEmployeeSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Employee Code *</label>
                      <Input
                        value={employeeForm.employee_code}
                        onChange={(e) => setEmployeeForm({...employeeForm, employee_code: e.target.value})}
                        placeholder="e.g., RB001"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Full Name *</label>
                      <Input
                        value={employeeForm.name}
                        onChange={(e) => setEmployeeForm({...employeeForm, name: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Email *</label>
                      <Input
                        type="email"
                        value={employeeForm.email}
                        onChange={(e) => setEmployeeForm({...employeeForm, email: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Phone</label>
                      <Input
                        value={employeeForm.phone}
                        onChange={(e) => setEmployeeForm({...employeeForm, phone: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Department *</label>
                      <Select value={employeeForm.department} onValueChange={(v) => setEmployeeForm({...employeeForm, department: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                        <SelectContent>
                          {DEPARTMENTS.map(d => (
                            <SelectItem key={d} value={d}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Job Title/Role *</label>
                      <Input
                        value={employeeForm.role}
                        onChange={(e) => setEmployeeForm({...employeeForm, role: e.target.value})}
                        placeholder="e.g., Sales Agent"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Employee Type *</label>
                      <Select value={employeeForm.employee_type} onValueChange={(v) => setEmployeeForm({...employeeForm, employee_type: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="staff">Staff (Fixed Salary)</SelectItem>
                          <SelectItem value="agent">Agent (Commission-based)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Contract Type *</label>
                      <Select value={employeeForm.contract_type} onValueChange={(v) => setEmployeeForm({...employeeForm, contract_type: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="permanent">Permanent</SelectItem>
                          <SelectItem value="contract">Contract</SelectItem>
                          <SelectItem value="part-time">Part-time</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Hire Date *</label>
                      <Input
                        type="date"
                        value={employeeForm.hire_date}
                        onChange={(e) => setEmployeeForm({...employeeForm, hire_date: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Salary Band</label>
                      <Select value={employeeForm.salary_band} onValueChange={(v) => setEmployeeForm({...employeeForm, salary_band: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select band" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="A">Band A (Executive)</SelectItem>
                          <SelectItem value="B">Band B (Senior)</SelectItem>
                          <SelectItem value="C">Band C (Mid-level)</SelectItem>
                          <SelectItem value="D">Band D (Junior)</SelectItem>
                          <SelectItem value="E">Band E (Entry)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Gross Salary (K) *</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={employeeForm.gross_salary}
                        onChange={(e) => setEmployeeForm({...employeeForm, gross_salary: e.target.value})}
                        required
                      />
                    </div>
                    {employeeForm.employee_type === "agent" && (
                      <div>
                        <label className="text-sm font-medium">Commission Rate (%)</label>
                        <Input
                          type="number"
                          step="0.1"
                          value={employeeForm.commission_rate}
                          onChange={(e) => setEmployeeForm({...employeeForm, commission_rate: e.target.value})}
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">Bank Details</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-medium">Bank Name *</label>
                        <Input
                          value={employeeForm.bank_name}
                          onChange={(e) => setEmployeeForm({...employeeForm, bank_name: e.target.value})}
                          placeholder="e.g., Zanaco"
                          required
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Account Number *</label>
                        <Input
                          value={employeeForm.account_number}
                          onChange={(e) => setEmployeeForm({...employeeForm, account_number: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Branch Code</label>
                        <Input
                          value={employeeForm.branch_code}
                          onChange={(e) => setEmployeeForm({...employeeForm, branch_code: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setEmployeeDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit">
                      {editingEmployee ? "Update Employee" : "Add Employee"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Gross Salary</TableHead>
                    <TableHead className="text-right">Net Salary</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No employees found. Add your first employee.
                      </TableCell>
                    </TableRow>
                  ) : (
                    employees.map((emp) => (
                      <TableRow key={emp.employee_id}>
                        <TableCell className="font-mono">{emp.employee_code}</TableCell>
                        <TableCell className="font-medium">{emp.name}</TableCell>
                        <TableCell>{emp.department}</TableCell>
                        <TableCell>{emp.role}</TableCell>
                        <TableCell>
                          <Badge variant={emp.employee_type === "agent" ? "secondary" : "outline"}>
                            {emp.employee_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(emp.gross_salary)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(emp.net_salary)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => handleEditEmployee(emp)} title="Edit">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => openDeletionDialog(emp)}
                              title={isAdmin ? "Delete" : "Request Deletion"}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payroll Tab */}
        <TabsContent value="payroll" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center">
              <Select value={viewMonth} onValueChange={setViewMonth}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={viewYear.toString()} onValueChange={(v) => setViewYear(parseInt(v))}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              {payrollRecords.length > 0 && (
                <Button variant="outline" onClick={handleSendAllPayslips}>
                  <Send className="h-4 w-4 mr-2" />
                  Email All Payslips
                </Button>
              )}
              {payrollRecords.some(r => r.status === "pending") && (
                <Button variant="outline" onClick={handleMarkAllAsPaid}>
                  <Check className="h-4 w-4 mr-2" />
                  Mark All Paid
                </Button>
              )}
              <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="generate-payroll-btn">
                    <Calendar className="h-4 w-4 mr-2" />
                    Generate Payroll
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Generate Monthly Payroll</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      This will generate payroll records for all active employees.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Month</label>
                        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {MONTHS.map(m => (
                              <SelectItem key={m} value={m}>{m}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium">Year</label>
                        <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[2024, 2025, 2026, 2027].map(y => (
                              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setGenerateDialogOpen(false)}>Cancel</Button>
                      <Button onClick={handleGeneratePayroll}>Generate</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Deductions Breakdown */}
          {payrollSummary && payrollSummary.total_employees > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Deductions Breakdown - {viewMonth} {viewYear}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">PAYE (Tax)</p>
                    <p className="text-lg font-bold">{formatCurrency(payrollSummary.total_paye)}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">NAPSA</p>
                    <p className="text-lg font-bold">{formatCurrency(payrollSummary.total_napsa)}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">NHIMA</p>
                    <p className="text-lg font-bold">{formatCurrency(payrollSummary.total_nhima)}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">Personal Levy</p>
                    <p className="text-lg font-bold">{formatCurrency(payrollSummary.total_personal_levy)}</p>
                  </div>
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <p className="text-xs text-muted-foreground">Total Deductions</p>
                    <p className="text-lg font-bold">{formatCurrency(payrollSummary.total_deductions)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">PAYE</TableHead>
                    <TableHead className="text-right">NAPSA</TableHead>
                    <TableHead className="text-right">NHIMA</TableHead>
                    <TableHead className="text-right">Net Pay</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payrollRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        No payroll records for {viewMonth} {viewYear}. Generate payroll to create records.
                      </TableCell>
                    </TableRow>
                  ) : (
                    payrollRecords.map((record) => (
                      <TableRow key={record.record_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{record.employee_name}</p>
                            <p className="text-xs text-muted-foreground">{record.employee_code}</p>
                          </div>
                        </TableCell>
                        <TableCell>{record.department}</TableCell>
                        <TableCell className="text-right">{formatCurrency(record.gross_salary)}</TableCell>
                        <TableCell className="text-right text-red-600">{formatCurrency(record.paye)}</TableCell>
                        <TableCell className="text-right text-red-600">{formatCurrency(record.napsa)}</TableCell>
                        <TableCell className="text-right text-red-600">{formatCurrency(record.nhima)}</TableCell>
                        <TableCell className="text-right font-bold">{formatCurrency(record.net_salary)}</TableCell>
                        <TableCell>
                          <Badge variant={record.status === "paid" ? "default" : "secondary"}>
                            {record.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {record.status === "pending" && (
                              <Button size="sm" variant="ghost" onClick={() => handleMarkAsPaid(record.record_id)} title="Mark as Paid">
                                <Check className="h-4 w-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => handleDownloadPayslip(record.record_id, record.employee_name)} title="Download Payslip">
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleSendPayslipEmail(record.record_id, record.employee_name)} 
                              title={record.email_sent ? "Resend Payslip" : "Email Payslip"}
                              className={record.email_sent ? "text-green-500" : ""}
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Payroll History</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Employees</TableHead>
                    <TableHead className="text-right">Total Gross</TableHead>
                    <TableHead className="text-right">Total Net</TableHead>
                    <TableHead className="text-center">Paid</TableHead>
                    <TableHead className="text-center">Pending</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payrollHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No payroll history yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    payrollHistory.map((h) => (
                      <TableRow key={`${h.month}-${h.year}`}>
                        <TableCell className="font-medium">{h.month} {h.year}</TableCell>
                        <TableCell className="text-right">{h.total_employees}</TableCell>
                        <TableCell className="text-right">{formatCurrency(h.total_gross)}</TableCell>
                        <TableCell className="text-right font-bold">{formatCurrency(h.total_net)}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="default">{h.paid_count}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {h.pending_count > 0 ? (
                            <Badge variant="secondary">{h.pending_count}</Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setViewMonth(h.month);
                              setViewYear(h.year);
                              setActiveTab("payroll");
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Deletion Requests Tab - Admin Only */}
        {isAdmin && (
          <TabsContent value="requests" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Employee Deletion Requests</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Requested At</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deletionRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                          No pending deletion requests.
                        </TableCell>
                      </TableRow>
                    ) : (
                      deletionRequests.map((req) => (
                        <TableRow key={req.request_id}>
                          <TableCell className="font-medium">{req.employee_name}</TableCell>
                          <TableCell>{req.reason || "No reason provided"}</TableCell>
                          <TableCell>{new Date(req.requested_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="default"
                                onClick={() => handleApproveDeletion(req.request_id)}
                              >
                                Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleRejectDeletion(req.request_id)}
                              >
                                Reject
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Deletion Confirmation Dialog */}
      <Dialog open={deletionDialogOpen} onOpenChange={setDeletionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isAdmin ? "Delete Employee" : "Request Employee Deletion"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {isAdmin 
                ? `Are you sure you want to delete ${deletionTarget?.name}? This will deactivate their account.`
                : `Request to delete ${deletionTarget?.name}. This requires admin approval.`
              }
            </p>
            {!isAdmin && (
              <div>
                <label className="text-sm font-medium">Reason for deletion *</label>
                <Input
                  value={deletionReason}
                  onChange={(e) => setDeletionReason(e.target.value)}
                  placeholder="e.g., Resignation, End of contract, Dismissal..."
                  required
                />
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeletionDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleRequestDeletion}
                disabled={!isAdmin && !deletionReason.trim()}
              >
                {isAdmin ? "Delete" : "Submit Request"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayrollDashboard;
